/**
 * v1.3.0 — Background TMDB poster warmer for the Series tiles table.
 *
 * WHY
 * ---
 * Providers often ship series episodes without a `tvg-logo` / `cover`
 * URL — the tile ends up with `poster_url IS NULL` in `series_tiles`.
 * The runtime PosterImage component then falls back to a per-tile
 * TMDB fetch on scroll, which never catches up on a fast Smart-TV
 * remote scroll → the purple placeholder problem the user videoed.
 *
 * FIX
 * ---
 * On playlist load (post-import), walk the `series_tiles` rows whose
 * `poster_url IS NULL`, resolve them via the same TMDB cache
 * (`getCachedTmdb`) with 3-wide concurrency, and UPDATE the tile so
 * subsequent reads see the resolved URL without any network round
 * trip.
 *
 * This runs at IDLE-priority — the app is fully interactive while the
 * warm progresses. Cancellation is per-playlist: if the user switches
 * playlists the previous warm is aborted.
 */
import { debugLog } from "./debugMode";
import { getCachedTmdb } from "./tmdbCache";
import {
  getSeriesMissingPoster,
  updateSeriesPoster,
  sqlIsAvailable,
} from "./cacheBackend";

const CONCURRENCY = 3;
const BATCH_LIMIT = 200;
const IDLE_DELAY_MS = 800;

const _activeCancel = new Map(); // playlistId -> AbortController

/**
 * Kick off (or replace) the warm for one playlist. Idempotent; if a
 * warm is already running for this playlist we cancel it first so we
 * don't get duplicate progress.
 */
export function startSeriesPosterWarm(playlistId) {
  if (!playlistId) return;
  if (!sqlIsAvailable()) return;
  // Cancel prior warm.
  const prev = _activeCancel.get(playlistId);
  if (prev) prev.abort();
  const controller = new AbortController();
  _activeCancel.set(playlistId, controller);

  const kickoff = async () => {
    try {
      let cycles = 0;
      let resolved = 0;
      let missed = 0;
      // Walk the queue in bounded batches so we never hold every row
      // in JS heap at once.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (controller.signal.aborted) break;
        const rows = await getSeriesMissingPoster(playlistId, BATCH_LIMIT);
        if (!rows || rows.length === 0) break;
        cycles += 1;

        // 3-wide worker pool.
        //
        // v1.3.1 — Dedupe by series_name inside each batch. One show
        // can live in multiple group rows now (Comedy + Recently
        // added, etc.), so we hit TMDB once and let the SQL UPDATE
        // fill every copy in one shot.
        const seen = new Set();
        const workQueue = rows.filter((r) => {
          const nm = String(r.series_name || "").toLowerCase();
          if (seen.has(nm)) return false;
          seen.add(nm);
          return true;
        });
        let idx = 0;
        async function worker() {
          while (idx < workQueue.length) {
            if (controller.signal.aborted) return;
            const r = workQueue[idx++];
            try {
              const tmdb = await getCachedTmdb(
                "series",
                r.series_name,
                r.year || undefined,
              );
              const url = tmdb?.poster || tmdb?.backdrop || null;
              if (url) {
                await updateSeriesPoster(
                  playlistId,
                  r.series_key,
                  url,
                  r.series_name,
                );
                resolved += 1;
              } else {
                missed += 1;
              }
            } catch {
              missed += 1;
            }
          }
        }
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, workQueue.length) }, () => worker()),
        );

        // If a full batch came back with zero resolutions, most of
        // the remaining rows are non-TMDB titles (regional providers
        // etc.) — stop chewing CPU/network on them.
        if (resolved === 0 && cycles >= 2) break;
      }
      debugLog(
        `[posterWarmer] playlist=${playlistId} cycles=${cycles} ` +
        `resolved=${resolved} missed=${missed}`,
      );
    } catch (e) {
      debugLog(`[posterWarmer] failed: ${e?.message || e}`);
    } finally {
      _activeCancel.delete(playlistId);
    }
  };

  // Defer to idle so we never step on the initial render.
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => setTimeout(kickoff, IDLE_DELAY_MS), { timeout: 5000 });
  } else {
    setTimeout(kickoff, IDLE_DELAY_MS);
  }
}

/** Cancel any pending warm for a playlist (used on playlist switch). */
export function stopSeriesPosterWarm(playlistId) {
  const c = _activeCancel.get(playlistId);
  if (c) c.abort();
  _activeCancel.delete(playlistId);
}
